import React, { useEffect } from 'react';
import Select, { components } from 'react-select';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

// Types
import { SlackChannelType } from 'Shared/types/SlackChannel.type';

// Components
import WpLabelled from 'Wpds/components/labelled/WpLabelled';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpBadge } from 'Wpds/components/badge';
import { WpButton } from 'Wpds/components/button';

// Styles
import './SlackWorkspaceSelect.scss';

type Props = {
  options: SlackChannelType[];
  selected: SlackChannelType[];
  onChange?: Function;
  errorMsg?: string;
  isDisabled?: boolean;
  label?: string;
  showLabel?: boolean;
  helpText?: string;
};

interface SlackChannelOptionType extends SlackChannelType {
  label: string;
  value: string;
}

const SlackWorkspaceSelect = ({
  options = [],
  selected = [],
  onChange,
  isDisabled = false,
  errorMsg,
  showLabel = true,
  label = 'Select Slack channels to use for AgendaAI',
  helpText,
}: Props) => {
  const formatOptions = (options: SlackChannelType[]): SlackChannelOptionType[] =>
    options.map(option => {
      return {
        value: option.name,
        label: option.name,
        ...option,
      };
    });

  const availableOptions: SlackChannelOptionType[] = formatOptions(options);
  const selectedOptions: SlackChannelOptionType[] = formatOptions(selected);

  const handleOnChange = (options: SlackChannelOptionType[]) => {
    if (!onChange) return;
    onChange(
      options.map(({ id, name, is_private }) => {
        return { id, name, is_private };
      })
    );
  };

  const handleDismissChannel = (item: SlackChannelOptionType) => {
    handleOnChange(selectedOptions.filter(option => option.id !== item.id));
  };

  useEffect(() => {
    options.map(option => {
      return { ...option, label: option.name, value: option.id };
    });
  }, []);

  const baseClassName = 'SlackWorkspaceSelect';

  return (
    <WpLabelled
      id="channel-input"
      label={label}
      showRequiredIndicator={false}
      error={errorMsg && errorMsg}
      labelHidden={!showLabel}
      helpText={helpText}
    >
      <Select
        isDisabled={isDisabled}
        isSearchable
        isMulti
        className={`${baseClassName}__chanenel-input__select`}
        menuPortalTarget={document.body}
        menuShouldScrollIntoView={false}
        menuPosition={'absolute'}
        styles={{
          control: baseStyles => ({
            ...baseStyles,
            border: errorMsg ? `1px solid ${ColorConstants.red[500]}` : '',
          }),
          menuPortal: base => ({ ...base, zIndex: 9999 }),
        }}
        components={{
          Option: props => {
            {
              return (
                <components.Option {...props} className={`${baseClassName}__option`}>
                  <div className={`${baseClassName}__option__icon`}>
                    <WpIcon name={props.data.is_private ? 'lock-alt' : 'hashtag'} size="sm" />
                  </div>
                  <span className={`${baseClassName}__option__label`}>{props.data.label}</span>
                </components.Option>
              );
            }
          },
          MultiValueContainer: props => {
            return (
              <div className={`${baseClassName}__channel-input__badge`}>
                <WpBadge color="blue" size="lg" onDismiss={() => handleDismissChannel(props.data)}>
                  <WpIcon name={props.data.is_private ? 'lock-alt' : 'hashtag'} size="sm" />
                  {props.data.label}
                </WpBadge>
              </div>
            );
          },
          ClearIndicator: props => (
            <components.ClearIndicator {...props}>
              <WpButton variant="tertiary" size="sm">
                <WpIcon name="times" />
              </WpButton>
            </components.ClearIndicator>
          ),
          DropdownIndicator: () => (
            <span className="DuplicateModal__search-icon">
              <WpIcon name="search" />
            </span>
          ),

          IndicatorSeparator: null,
        }}
        options={availableOptions}
        placeholder="Search channels"
        onChange={handleOnChange}
        value={selectedOptions}
      />
    </WpLabelled>
  );
};

export default SlackWorkspaceSelect;
